import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Action } from 'history';
import { browserHistory } from '@cbridge-utils/app';
import { isUnsavedChangesConfirmationSkipped } from '@cbridge-packages/routing';
import { useConfirmNavigateAway } from './useConfirmNavigateAway';

/**
 * Blocks in-app navigation (links, back/forward, Redux push/replace)
 * and tab close/refresh while `when` is true. Shows a confirmation dialog
 * using the app's DialogsProvider.
 *
 * `shouldBlockNow` re-checks the condition at the moment navigation is attempted:
 * `block` may be stale, since disarming requires a re-render that can happen
 * after the navigation (e.g. redirect right after a successful form submit).
 *
 * A PUSH/REPLACE marked with `withSkipUnsavedChangesConfirmation` (e.g. closing the
 * sidebar of an entity being deleted) passes without the dialog. POP is never skipped:
 * the marker stays in the history entry and must not disarm a later Back/Forward.
 */
export function useUnsavedChangesConfirmation(block: boolean, shouldBlockNow?: () => boolean) {
  const { t } = useTranslation();

  const { confirmNavigateAway } = useConfirmNavigateAway();

  useEffect(() => {
    if (!block) return undefined;

    // Block SPA navigations through the shared history instance
    const unblock = browserHistory.block(async (tx) => {
      const skipConfirmation = (shouldBlockNow && !shouldBlockNow())
        || tx.location.pathname === window.location.pathname
        || (tx.action !== Action.Pop && isUnsavedChangesConfirmationSkipped(tx.location.state));

      if (skipConfirmation || (await confirmNavigateAway())) {
        unblock();
        setTimeout(tx.retry, 0);
      }
      // If not confirmed, do nothing -> stay on the page
    });

    // Block tab close/refresh
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldBlockNow && !shouldBlockNow()) return;
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      unblock();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [block, t, confirmNavigateAway, shouldBlockNow]);
}
